import React, { useCallback, useEffect, useRef } from 'react';
import { Select, Form } from 'antd';
import { LocationDTO } from '@/shared/api/model';
import useLocation from '../hooks/useLocation';

export interface LocationCascadeProps {
    disabled?: boolean;
    showLabels?: boolean;
    required?: {
        zone?: boolean;
        region?: boolean;
        district?: boolean;
        ward?: boolean;
        street?: boolean;
    };
    zone?: boolean;
    region?: boolean;
    district?: boolean;
    ward?: boolean;
    street?: boolean;
    onSelectionChange?: (selections: {
        zone?: LocationDTO;
        region?: LocationDTO;
        district?: LocationDTO;
        ward?: LocationDTO;
        street?: LocationDTO;
    }) => void;
    initialValues?: {
        zoneId?: number;
        regionId?: number;
        districtId?: number;
        wardId?: number;
        streetId?: number;
    };
}

const LocationCascade: React.FC<LocationCascadeProps> = ({
                                                             disabled = false,
                                                             showLabels = true,
                                                             required = {},
                                                             zone = true,
                                                             region = true,
                                                             district = true,
                                                             ward = true,
                                                             street = true,
                                                             onSelectionChange,
                                                             initialValues
                                                         }) => {
    const {
        zones,
        regions,
        districts,
        wards,
        streets,
        selectedZone,
        selectedRegion,
        selectedDistrict,
        selectedWard,
        selectedStreet,
        handleZoneChange,
        handleRegionChange,
        handleDistrictChange,
        handleWardChange,
        handleStreetChange,
        isLoading,
        loadLocationHierarchy
    } = useLocation();

    // Use ref to track previous values to prevent unnecessary updates
    const prevSelectionsRef = useRef({
        zoneId: selectedZone?.id,
        regionId: selectedRegion?.id,
        districtId: selectedDistrict?.id,
        wardId: selectedWard?.id,
        streetId: selectedStreet?.id
    });

    // Load initial values if provided
    useEffect(() => {
        const loadInitialValues = async () => {
            if (initialValues) {
                // If we have a street ID, load the hierarchy from the bottom up
                if (initialValues.streetId) {
                    await loadLocationHierarchy(initialValues.streetId);
                }
                // If we have a ward ID but no street ID
                else if (initialValues.wardId) {
                    await loadLocationHierarchy(initialValues.wardId);
                }
                // If we have a district ID but no ward or street ID
                else if (initialValues.districtId) {
                    await loadLocationHierarchy(initialValues.districtId);
                }
                // If we have a region ID but no district, ward, or street ID
                else if (initialValues.regionId) {
                    await loadLocationHierarchy(initialValues.regionId);
                }
                // If we have only a zone ID
                else if (initialValues.zoneId) {
                    await loadLocationHierarchy(initialValues.zoneId);
                }
            }
        };

        if (initialValues) {
            loadInitialValues();
        }
    }, [initialValues, loadLocationHierarchy]);

    // Notify parent about selection changes - with optimization to prevent infinite loops
    useEffect(() => {
        // Skip if no handler or if selections haven't actually changed
        if (!onSelectionChange) return;

        const currentSelections = {
            zoneId: selectedZone?.id,
            regionId: selectedRegion?.id,
            districtId: selectedDistrict?.id,
            wardId: selectedWard?.id,
            streetId: selectedStreet?.id
        };

        // Compare current with previous selections
        const hasChanged =
            currentSelections.zoneId !== prevSelectionsRef.current.zoneId ||
            currentSelections.regionId !== prevSelectionsRef.current.regionId ||
            currentSelections.districtId !== prevSelectionsRef.current.districtId ||
            currentSelections.wardId !== prevSelectionsRef.current.wardId ||
            currentSelections.streetId !== prevSelectionsRef.current.streetId;

        // Only call onSelectionChange if values have actually changed
        if (hasChanged) {
            // Update ref with current values
            prevSelectionsRef.current = currentSelections;

            // Call the handler with current selections
            onSelectionChange({
                // @ts-ignore
                zone: selectedZone,
                // @ts-ignore
                region: selectedRegion,
                // @ts-ignore
                district: selectedDistrict,
                // @ts-ignore
                ward: selectedWard,
                // @ts-ignore
                street: selectedStreet
            });
        }
    }, [selectedZone, selectedRegion, selectedDistrict, selectedWard, selectedStreet, onSelectionChange]);

    // Render location options
    const renderLocationOptions = useCallback((locations: LocationDTO[]) => {
        return locations.map((loc) => (
            <Select.Option key={loc.id} value={loc.id} title={loc.name}>
                {loc.name}
            </Select.Option>
        ));
    }, []);

    return (
        <div className="location-cascade">
            {/* Zone Selector */}
            {zone && (
                <Form.Item
                    label={showLabels ? "Zone" : ""}
                    rules={[{ required: required.zone, message: 'Please select a zone' }]}
                    className="mb-2"
                >
                    <Select
                        placeholder="Select Zone"
                        value={selectedZone?.id}
                        onChange={(value) => {
                            const zone = zones.find(z => z.id === value) || undefined;
                            handleZoneChange(zone);
                        }}
                        disabled={disabled || isLoading}
                        loading={isLoading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.title as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        className="w-full"
                    >
                        {renderLocationOptions(zones)}
                    </Select>
                </Form.Item>
            )}

            {/* Region Selector */}
            {region && (
                <Form.Item
                    label={showLabels ? "Region" : ""}
                    rules={[{ required: required.region, message: 'Please select a region' }]}
                    className="mb-2"
                >
                    <Select
                        placeholder="Select Region"
                        value={selectedRegion?.id}
                        onChange={(value) => {
                            const region = regions.find(r => r.id === value) || undefined;
                            handleRegionChange(region);
                        }}
                        disabled={disabled || !selectedZone || isLoading || regions.length === 0}
                        // @ts-ignore
                        loading={isLoading && selectedZone && regions.length === 0}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.title as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        className="w-full"
                    >
                        {renderLocationOptions(regions)}
                    </Select>
                </Form.Item>
            )}

            {/* District Selector */}
            {district && (
                <Form.Item
                    label={showLabels ? "District" : ""}
                    rules={[{ required: required.district, message: 'Please select a district' }]}
                    className="mb-2"
                >
                    <Select
                        placeholder="Select District"
                        value={selectedDistrict?.id}
                        onChange={(value) => {
                            const district = districts.find(d => d.id === value) || undefined;
                            handleDistrictChange(district);
                        }}
                        disabled={disabled || !selectedRegion || isLoading || districts.length === 0}
                        // @ts-ignore
                        loading={isLoading && selectedRegion && districts.length === 0}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.title as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        className="w-full"
                    >
                        {renderLocationOptions(districts)}
                    </Select>
                </Form.Item>
            )}

            {/* Ward Selector */}
            {ward && (
                <Form.Item
                    label={showLabels ? "Ward" : ""}
                    rules={[{ required: required.ward, message: 'Please select a ward' }]}
                    className="mb-2"
                >
                    <Select
                        placeholder="Select Ward"
                        value={selectedWard?.id}
                        onChange={(value) => {
                            const ward = wards.find(w => w.id === value) || undefined;
                            handleWardChange(ward);
                        }}
                        disabled={disabled || !selectedDistrict || isLoading || wards.length === 0}
                        // @ts-ignore
                        loading={isLoading && selectedDistrict && wards.length === 0}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.title as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        className="w-full"
                    >
                        {renderLocationOptions(wards)}
                    </Select>
                </Form.Item>
            )}

            {/* Street Selector */}
            {street && (
                <Form.Item
                    label={showLabels ? "Street" : ""}
                    rules={[{ required: required.street, message: 'Please select a street' }]}
                    className="mb-2"
                >
                    <Select
                        placeholder="Select Street"
                        value={selectedStreet?.id}
                        onChange={(value) => {
                            const street = streets.find(s => s.id === value) || undefined;
                            handleStreetChange(street);
                        }}
                        disabled={disabled || !selectedWard || isLoading || streets.length === 0}
                        // @ts-ignore
                        loading={isLoading && selectedWard && streets.length === 0}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.title as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        className="w-full"
                    >
                        {renderLocationOptions(streets)}
                    </Select>
                </Form.Item>
            )}
        </div>
    );
};


export default LocationCascade;
